// src/pages/partners/partners-logged-in/routes/dashboard/first-col.tsx
// Adds Workout Duration (7d) bar chart and uses #023020 as the accent across cards/charts.
// Normalizes all dates to LOCAL timezone days and multiplies meal macros by servings.
import React, { useMemo } from "react";
import {
  Box,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import CakeIcon from "@mui/icons-material/Cake";
import HeightIcon from "@mui/icons-material/Height";
import ScaleIcon from "@mui/icons-material/MonitorWeight";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import TimelineIcon from "@mui/icons-material/Timeline";
import GlassContainer from "../../../components/liquid-glass";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RTooltip,
  CartesianGrid,
} from "recharts";

/** Minimal shapes */
export type WorkoutLite = {
  id: string;
  dayOfExecution: string;
  durationInMinutes?: number;
};
export type MealLite = {
  id: string;
  dateTime: string;
  calories?: number;
  protein?: number;
  carbs?: number;
  fats?: number;
  servings?: number; // number of servings eaten in that log
};
export type ClientLite = {
  userId: string;
  name?: string;
  email?: string;
  gender?: "male" | "female" | string;
  height?: number;
  weight?: number | number[];
  birthday?: Date | string;
  isMetricUnits?: boolean;
  workouts?: WorkoutLite[];
  meals?: MealLite[];
  goals?: {
    trainingGoal?:
    | "weight loss"
    | "hyperthrophy"
    | "strength"
    | "conditioning"
    | "maintain"
    | string;
    caloriesGoal?: number;
    proteinGoal?: number;
    carbsGoal?: number;
    fatsGoal?: number;
  };
};

const ACCENT = "#127351"; // theme primary – neutral in both modes

/**
 * Helpers for LOCAL day math
 */

// now in local tz
const nowLocal = () => new Date();

// local midnight N days ago (0 = today @ 00:00 local)
const daysAgoLocal = (n: number) => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - n);
  return d;
};

// to YYYY-MM-DD using local time, not UTC
function toLocalYMD(dateLike: string | number | Date): string {
  const d = new Date(dateLike);
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  const day = d.getDate();
  const mm = String(m).padStart(2, "0");
  const dd = String(day).padStart(2, "0");
  return `${y}-${mm}-${dd}`;
}

function ymdLocal(d: Date) {
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  const day = d.getDate();
  const mm = String(m).padStart(2, "0");
  const dd = String(day).padStart(2, "0");
  return `${y}-${mm}-${dd}`;
}

// inclusive range on actual Date objects
function inRangeLocal(dateISO?: string, start?: Date, end?: Date): boolean {
  if (!dateISO) return false;
  const d = new Date(dateISO);
  return (!start || d >= start) && (!end || d <= end);
}

// build last N local-calendar days oldest->newest
function lastNDatesLocal(n: number) {
  const arr: string[] = [];
  for (let i = n - 1; i >= 0; i--) {
    arr.push(ymdLocal(daysAgoLocal(i)));
  }
  return arr;
}

function MetricCard({
  title,
  icon,
  children,
  sub,
  loading = false,
}: {
  title: string;
  icon?: React.ReactNode;
  children?: React.ReactNode;
  sub?: React.ReactNode;
  loading?: boolean;
}) {
  return (
    <GlassContainer
      blur={22}
      radius={18}
      isMetallic
      sx={{
        height: "100%",
        flexDirection: "column",
        p: 1.5,
        border: `1px solid ${ACCENT}22`,
      }}
    >
      <CardHeader
        title={
          <Typography
            variant="subtitle1"
            fontWeight={800}
            sx={{ color: ACCENT }}
          >
            {title}
          </Typography>
        }
        subheader={sub}
        sx={{ pb: 0.5 }}
      />
      <CardContent sx={{ width: "100%" }}>
        {loading ? (
          <Stack gap={1}>
            <Skeleton variant="text" width="45%" />
            <Skeleton variant="rounded" height={20} />
            <Skeleton variant="rounded" height={20} />
            <Skeleton variant="rounded" height={20} />
          </Stack>
        ) : (
          children
        )}
      </CardContent>
    </GlassContainer>
  );
}

function average(arr: number[]): number {
  if (!arr.length) return 0;
  return arr.reduce((a, b) => a + b, 0) / arr.length;
}

/**
 * Build per-day totals for calories/protein/carbs/fats over last 7 local days.
 * MULTIPLIES BY SERVINGS.
 */
function dailySumSeries(
  dates: string[],
  meals: MealLite[] | undefined,
  key: "calories" | "protein" | "carbs" | "fats"
) {
  const map = new Map<string, number>(dates.map((d) => [d, 0]));

  (meals ?? [])
    .filter((m) => inRangeLocal(m.dateTime, daysAgoLocal(6), nowLocal()))
    .forEach((m) => {
      const localDay = toLocalYMD(m.dateTime);
      if (!map.has(localDay)) return;

      const servings = m.servings ?? 1;
      const baseVal = m[key] ?? 0;
      const totalForMeal = baseVal * servings;

      map.set(localDay, (map.get(localDay) ?? 0) + totalForMeal);
    });

  return dates.map((d) => ({
    day: d.slice(5),
    value: Math.floor(map.get(d) ?? 0), // ⬅️ floor each daily total
  }));
}

function dailyWorkoutMinutesSeries(
  dates: string[],
  workouts: WorkoutLite[] | undefined
) {
  const map = new Map<string, number>(dates.map((d) => [d, 0]));

  (workouts ?? [])
    .filter((w) => inRangeLocal(w.dayOfExecution, daysAgoLocal(6), nowLocal()))
    .forEach((w) => {
      const localDay = toLocalYMD(w.dayOfExecution);
      if (!map.has(localDay)) return;
      map.set(localDay, (map.get(localDay) ?? 0) + (w.durationInMinutes ?? 0));
    });

  return dates.map((d) => ({
    day: d.slice(5),
    value: Math.floor(map.get(d) ?? 0), // ⬅️ floor each daily total
  }));
}

export function ClientOverview({ u }: { u: ClientLite }) {
  const theme = useTheme();
  const gridStroke = theme.palette.mode === "light" ? "#eee" : "#333";
  const tickStyle = { fontSize: 12, fill: theme.palette.text.secondary };

  // last 7 local calendar days, oldest -> newest
  const dates7 = lastNDatesLocal(7);

  // workouts last 7d local
  const workoutsLast7 = (u.workouts ?? []).filter((w) =>
    inRangeLocal(w.dayOfExecution, daysAgoLocal(6), nowLocal())
  );
  const avgDurationPerWorkout = average(
    workoutsLast7
      .map((w) => w.durationInMinutes || 0)
      .filter((n) => typeof n === "number")
  );

  // per-day workout minutes
  const workoutMinutesSeries = useMemo(
    () => dailyWorkoutMinutesSeries(dates7, u.workouts),
    [u.workouts, dates7]
  );
  const avgMinutesPerDay = Math.round(
    average(workoutMinutesSeries.map((d) => d.value))
  );

  // nutrition (servings-aware, local buckets)
  const calSeries = useMemo(
    () => dailySumSeries(dates7, u.meals, "calories"),
    [u.meals, dates7]
  );
  const proSeries = useMemo(
    () => dailySumSeries(dates7, u.meals, "protein"),
    [u.meals, dates7]
  );
  const carbSeries = useMemo(
    () => dailySumSeries(dates7, u.meals, "carbs"),
    [u.meals, dates7]
  );
  const fatSeries = useMemo(
    () => dailySumSeries(dates7, u.meals, "fats"),
    [u.meals, dates7]
  );

  const avgCalories = Math.round(average(calSeries.map((d) => d.value)));
  const avgProtein = Math.round(average(proSeries.map((d) => d.value)));
  const avgCarbs = Math.round(average(carbSeries.map((d) => d.value)));
  const avgFats = Math.round(average(fatSeries.map((d) => d.value)));

  // Body stats
  const weightVal = Array.isArray(u.weight)
    ? u.weight[u.weight.length - 1]
    : u.weight;
  const heightLabel =
    u.height != null
      ? (() => {
        const totalInches = u.height / 2.54;
        const feet = Math.floor(totalInches / 12);
        const inches = Math.round(totalInches % 12);
        return `${feet}′ ${inches}″`;
      })()
      : "—";
  const weightLabel =
    weightVal != null ? `${(2.20462 * Number(weightVal)).toFixed(1)} lb` : "—";
  const bday = u.birthday ? new Date(u.birthday).toLocaleDateString() : "—";
  const goalLabel = u.goals?.trainingGoal
    ? String(u.goals.trainingGoal).toUpperCase()
    : "—";

  const Chart = ({
    data,
    unit,
  }: {
    data: { day: string; value: number }[];
    unit?: string;
  }) => (
    <Box sx={{ width: "100%", height: 160 }}>
      <ResponsiveContainer>
        <BarChart
          data={data}
          margin={{ top: 8, right: 16, bottom: 8, left: 8 }}
        >
          <CartesianGrid stroke={gridStroke} strokeDasharray="3 3" />
          <XAxis dataKey="day" tick={tickStyle} />
          <YAxis allowDecimals={false} tick={tickStyle} />
          <RTooltip
            content={({ active, payload, label }) =>
              active && payload && payload.length ? (
                <Box
                  sx={{
                    bgcolor: "background.paper",
                    border: (t) => `1px solid ${t.palette.divider}`,
                    borderRadius: 1.5,
                    p: 1,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {label}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {(payload[0] as any).payload?.value} {unit || ""}
                  </Typography>
                </Box>
              ) : null
            }
          />
          <Bar
            dataKey="value"
            isAnimationActive
            animationDuration={700}
            radius={[6, 6, 0, 0]}
            fill={ACCENT}
          />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );

  return (
    <Grid container spacing={2} sx={{ p: 1 }}>
      {/* Workout Duration per Day (7d) */}
      <Grid item xs={12}>
        <MetricCard
          title={`Workouts `}
          icon={<AccessTimeIcon />}
          sub={
            <Typography variant="caption" color="text.secondary">
              {Math.round(avgDurationPerWorkout)} min/workout
            </Typography>
          }
        >
          <Chart data={workoutMinutesSeries} unit="min" />
        </MetricCard>
      </Grid>

      <Grid item xs={12}>
        <MetricCard
          title={`Calories`}
          icon={<RestaurantIcon />}
          sub={
            <Typography variant="caption" color="text.secondary">
              {`${avgCalories}kcal`}
            </Typography>
          }
        >
          <Chart data={calSeries} unit="kcal" />
        </MetricCard>
      </Grid>

      <Grid item xs={12}>
        <MetricCard
          title={`Protein`}
          icon={<RestaurantIcon />}
          sub={
            <Typography variant="caption" color="text.secondary">
              {avgProtein}g
            </Typography>
          }
        >
          <Chart data={proSeries} unit="g" />
        </MetricCard>
      </Grid>

      <Grid item xs={12}>
        <MetricCard
          title={`Carbs`}
          icon={<RestaurantIcon />}
          sub={
            <Typography variant="caption" color="text.secondary">
              {avgCarbs}g
            </Typography>
          }
        >
          <Chart data={carbSeries} unit="g" />
        </MetricCard>
      </Grid>

      <Grid item xs={12}>
        <MetricCard
          title={`Fats`}
          icon={<RestaurantIcon />}
          sub={
            <Typography variant="caption" color="text.secondary">
              {avgFats}g
            </Typography>
          }
        >
          <Chart data={fatSeries} unit="g" />
        </MetricCard>
      </Grid>

      <Grid item xs={12}>
        <MetricCard title="Body Stats" icon={<HeightIcon />}>
          <Stack
            direction="row"
            spacing={3}
            alignItems="center"
            flexWrap="wrap"
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <HeightIcon fontSize="small" />
              <Typography fontWeight={700}>{heightLabel}</Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <ScaleIcon fontSize="small" />
              <Typography fontWeight={700}>{weightLabel}</Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <CakeIcon fontSize="small" />
              <Typography fontWeight={700}>{bday}</Typography>
            </Stack>
          </Stack>
        </MetricCard>
      </Grid>
    </Grid>
  );
}
